// @flow

import type { Saga } from 'redux-saga';
import { call } from 'typed-redux-saga/macro';

import { isEnabled } from 'Config';
import { getInstance as getConfigServiceInstance } from 'Lib/ConfigService';
import { LEVER_EVENTS_SDK_IS_ENABLED } from 'Lib/ConfigService/levers';
import { withErrorHandling } from 'Lib/sagaHelpers';

import type { AnalyticsEventActionType } from 'Store/sparks/reporter';

let unsubscribeFn, rapidfireMigrationLever;

// On test, only send fallback when enabled. On prod use lever.
const getSendProtobufEvents = () => {
  if (__PRODTEST__ || __DEVELOPMENT__ || __TEST__) {
    return isEnabled('analyticsProtobufEvents');
  }
  if (!unsubscribeFn) {
    // set up subscription to config service
    const configService = getConfigServiceInstance();
    rapidfireMigrationLever = configService.readLever(LEVER_EVENTS_SDK_IS_ENABLED);
    unsubscribeFn = configService.subscribeLever(LEVER_EVENTS_SDK_IS_ENABLED, () => {
      rapidfireMigrationLever = configService.readLever(LEVER_EVENTS_SDK_IS_ENABLED);
    });
    window.addEventListener('beforeunload', () => {
      unsubscribeFn();
    });
  }
  return rapidfireMigrationLever;
};

// lazy load optional test analytics reporter and publisher
const loadAnalyticsSDK = async () => {
  const sdk = await import('Store/sparks/reporter' /* webpackChunkName: "async-analytics-sdk" */);
  return sdk;
};

/**
 * Console log error in dev/staging, throw in test, ignore in prod
 */
function* logErrorInDev(e) {
  if (__DEVELOPMENT__ || __PRODTEST__) {
    console.error(e);
  }
  if (__TEST__) {
    throw e;
  }
  yield;
}

/**
 * Defer loading of analytics reporter saga if enabled.
 */
export const asyncLoadedAnalyticsReporterSaga = withErrorHandling(
  function* _asyncLoadedAnalyticsReporterSaga(action: AnalyticsEventActionType): Saga<void> {
    if (getSendProtobufEvents()) {
      const { analyticsReporterSaga } = yield* call(loadAnalyticsSDK);
      yield* call(analyticsReporterSaga, action);
    }
  },
  {
    name: 'asyncLoadedAnalyticsReporterSaga',
    errorHandler: logErrorInDev,
  }
);
